import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  Search,
  Filter,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  Minus,
  Code,
  ExternalLink,
  Play,
} from 'lucide-react';
import { api } from '../../services/api';
import { TimeAgo, CorrelationIdBadge } from '../../components/admin/shared';

type TabType = 'timeline' | 'patterns' | 'trace';

export default function ErrorInvestigation() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  interface ErrorEntry {
    id: number;
    correlationId: string;
    seedId: number | null;
    seedName: string | null;
    storeId: string | null;
    runType: string | null;
    errorPhase: string | null;
    errorName: string | null;
    errorMessage: string | null;
    errorStack: string | null;
    pageUrl: string | null;
    pageTitle: string | null;
    pageState: Record<string, unknown> | null;
    retryCount: number;
    isRecoverable: boolean;
    createdAt: string;
  }
  const [activeTab, setActiveTab] = useState<TabType>('timeline');
  const [selectedError, setSelectedError] = useState<ErrorEntry | null>(null);
  const [traceCorrelationId, setTraceCorrelationId] = useState<string>(
    searchParams.get('correlationId') || ''
  );
  const [searchCorrelationId, setSearchCorrelationId] = useState('');

  // Filters
  const [seedFilter, setSeedFilter] = useState<string>('');
  const [storeFilter, setStoreFilter] = useState<string>('');
  const [phaseFilter, setPhaseFilter] = useState<string>('');
  const [hoursFilter, setHoursFilter] = useState<number>(24);

  // Queries
  const { data: timeline, isLoading: timelineLoading, refetch: refetchTimeline } = useQuery({
    queryKey: ['error-timeline', seedFilter, storeFilter, phaseFilter, hoursFilter],
    queryFn: () => api.getErrorTimeline({
      seedId: seedFilter ? parseInt(seedFilter) : undefined,
      storeId: storeFilter || undefined,
      phase: phaseFilter || undefined,
      hours: hoursFilter,
      limit: 100,
    }),
    enabled: activeTab === 'timeline',
  });

  const { data: patterns, isLoading: patternsLoading } = useQuery({
    queryKey: ['error-patterns', hoursFilter],
    queryFn: () => api.getErrorPatterns(hoursFilter),
    enabled: activeTab === 'patterns',
  });

  const { data: trace, isLoading: traceLoading } = useQuery({
    queryKey: ['error-trace', traceCorrelationId],
    queryFn: () => api.getErrorTrace(traceCorrelationId),
    enabled: activeTab === 'trace' && !!traceCorrelationId,
  });

  const { data: phases } = useQuery({
    queryKey: ['error-phases'],
    queryFn: () => api.getErrorPhases(),
  });

  const { data: summary } = useQuery({
    queryKey: ['error-summary', hoursFilter],
    queryFn: () => api.getErrorSummary(hoursFilter),
  });

  // Retry mutation
  const retryMutation = useMutation({
    mutationFn: ({ seedId, storeId, runType }: { seedId: number; storeId?: string; runType?: string }) =>
      api.retryFailedSeed(seedId, storeId, runType),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['error-timeline'] });
    },
  });

  const handleTraceSearch = () => {
    if (searchCorrelationId) {
      setTraceCorrelationId(searchCorrelationId);
      setActiveTab('trace');
      setSearchParams({ correlationId: searchCorrelationId });
    }
  };

  const handleCorrelationIdClick = (correlationId: string) => {
    setTraceCorrelationId(correlationId);
    setSearchCorrelationId(correlationId);
    setActiveTab('trace');
    setSearchParams({ correlationId });
  };

  const getPhaseColor = (phase: string | null) => {
    switch (phase) {
      case 'browser_launch':
        return 'bg-purple-100 text-purple-800';
      case 'navigation':
        return 'bg-blue-100 text-blue-800';
      case 'parsing':
        return 'bg-amber-100 text-amber-800';
      case 'contribution':
        return 'bg-green-100 text-green-800';
      case 'db_update':
        return 'bg-cyan-100 text-cyan-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getLogLevelColor = (level: string) => {
    switch (level) {
      case 'error':
        return 'text-red-600 bg-red-50';
      case 'warn':
        return 'text-amber-600 bg-amber-50';
      case 'info':
        return 'text-blue-600 bg-blue-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with search */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Error Investigation</h2>
          <p className="text-sm text-gray-500">
            {summary?.total || 0} errors in the last {hoursFilter} hours
          </p>
        </div>

        {/* Correlation ID Search */}
        <div className="flex gap-2">
          <div className="relative">
            <input
              type="text"
              value={searchCorrelationId}
              onChange={(e) => setSearchCorrelationId(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleTraceSearch()}
              placeholder="Search correlation ID..."
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 w-64 font-mono"
            />
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          </div>
          <button
            onClick={handleTraceSearch}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm font-medium"
          >
            Trace
          </button>
        </div>
      </div>

      {/* Summary Bar */}
      {summary && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex flex-wrap gap-4">
            {Object.entries(summary.byPhase).map(([phase, count]) => (
              <button
                key={phase}
                onClick={() => {
                  setPhaseFilter(phase);
                  setActiveTab('timeline');
                }}
                className={`px-3 py-1.5 rounded-full text-sm font-medium ${getPhaseColor(phase)} hover:opacity-80 transition-opacity`}
              >
                {phase}: {count}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-6">
          {(['timeline', 'patterns', 'trace'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`
                py-3 px-1 text-sm font-medium border-b-2 transition-colors
                ${activeTab === tab
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
                }
              `}
            >
              {tab === 'timeline' ? 'Timeline' : tab === 'patterns' ? 'Patterns' : 'Trace'}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'timeline' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-600">Filters:</span>
              </div>

              <select
                value={hoursFilter}
                onChange={(e) => setHoursFilter(parseInt(e.target.value))}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
              >
                <option value={1}>Last 1 hour</option>
                <option value={6}>Last 6 hours</option>
                <option value={24}>Last 24 hours</option>
                <option value={48}>Last 48 hours</option>
                <option value={168}>Last 7 days</option>
              </select>

              <select
                value={phaseFilter}
                onChange={(e) => setPhaseFilter(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
              >
                <option value="">All phases</option>
                {phases?.phases.map((phase) => (
                  <option key={phase} value={phase}>
                    {phase}
                  </option>
                ))}
              </select>

              <input
                type="text"
                value={seedFilter}
                onChange={(e) => setSeedFilter(e.target.value)}
                placeholder="Seed ID"
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm w-24"
              />

              <input
                type="text"
                value={storeFilter}
                onChange={(e) => setStoreFilter(e.target.value)}
                placeholder="Store ID"
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm w-24"
              />

              {(seedFilter || storeFilter || phaseFilter) && (
                <button
                  onClick={() => {
                    setSeedFilter('');
                    setStoreFilter('');
                    setPhaseFilter('');
                  }}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Clear filters
                </button>
              )}

              <button
                onClick={() => refetchTimeline()}
                className="ml-auto flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>
            </div>
          </div>

          {/* Error List */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 divide-y divide-gray-200">
            {timelineLoading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-6 h-6 text-gray-400 animate-spin" />
              </div>
            ) : (timeline?.errors?.length || 0) === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <AlertCircle className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                <p>No errors found</p>
              </div>
            ) : (
              timeline?.errors.map((error) => (
                <button
                  type="button"
                  key={error.id}
                  className="w-full text-left p-4 hover:bg-gray-50 cursor-pointer"
                  onClick={() => setSelectedError(selectedError?.id === error.id ? null : error)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${getPhaseColor(error.errorPhase)}`}>
                          {error.errorPhase || 'unknown'}
                        </span>
                        <span className="text-sm font-medium text-gray-900">
                          {error.errorName || 'Error'}
                        </span>
                        <span className="text-xs text-gray-500">
                          {error.seedName || `Seed ${error.seedId}`} / {error.storeId}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 truncate">
                        {error.errorMessage}
                      </p>
                      <div className="flex items-center gap-3 mt-2">
                        <CorrelationIdBadge
                          correlationId={error.correlationId}
                          onClick={() => handleCorrelationIdClick(error.correlationId)}
                        />
                        <TimeAgo timestamp={error.createdAt} className="text-xs" />
                        {error.retryCount > 0 && (
                          <span className="text-xs text-amber-600">
                            Retried {error.retryCount}x
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      {error.seedId && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            retryMutation.mutate({
                              seedId: error.seedId!,
                              storeId: error.storeId || undefined,
                              runType: error.runType || undefined,
                            });
                          }}
                          disabled={retryMutation.isPending}
                          className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                          title="Retry"
                        >
                          <Play className="w-4 h-4" />
                        </button>
                      )}
                      {selectedError?.id === error.id ? (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </div>

                  {/* Expanded Error Detail */}
                  {selectedError?.id === error.id && (
                    <div className="mt-4 pt-4 border-t border-gray-200 space-y-4">
                      {/* Stack Trace */}
                      {error.errorStack && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-700 mb-2">Stack Trace</h4>
                          <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg text-xs overflow-x-auto max-h-64">
                            {error.errorStack}
                          </pre>
                        </div>
                      )}

                      {/* Page Info */}
                      {(error.pageUrl || error.pageTitle) && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-700 mb-2">Page Info</h4>
                          <div className="bg-gray-50 p-3 rounded-lg text-sm">
                            {error.pageTitle && <p><strong>Title:</strong> {error.pageTitle}</p>}
                            {error.pageUrl && (
                              <p className="flex items-center gap-2">
                                <strong>URL:</strong>
                                <a
                                  href={error.pageUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-primary-600 hover:underline flex items-center gap-1"
                                >
                                  {error.pageUrl}
                                  <ExternalLink className="w-3 h-3" />
                                </a>
                              </p>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Page State */}
                      {error.pageState && Object.keys(error.pageState).length > 0 && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-700 mb-2">Page State</h4>
                          <pre className="bg-gray-50 p-3 rounded-lg text-xs overflow-x-auto max-h-48">
                            {JSON.stringify(error.pageState, null, 2)}
                          </pre>
                        </div>
                      )}

                      <div className="flex justify-end">
                        <button
                          onClick={() => handleCorrelationIdClick(error.correlationId)}
                          className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                        >
                          View full trace
                        </button>
                      </div>
                    </div>
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}

      {activeTab === 'patterns' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Errors grouped by type and phase
            </p>
            <select
              value={hoursFilter}
              onChange={(e) => setHoursFilter(parseInt(e.target.value))}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
            >
              <option value={6}>Last 6 hours</option>
              <option value={24}>Last 24 hours</option>
              <option value={48}>Last 48 hours</option>
              <option value={168}>Last 7 days</option>
            </select>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 divide-y divide-gray-200">
            {patternsLoading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-6 h-6 text-gray-400 animate-spin" />
              </div>
            ) : (patterns?.patterns?.length || 0) === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <AlertCircle className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                <p>No error patterns found</p>
              </div>
            ) : (
              patterns?.patterns.map((pattern, idx) => (
                <div key={idx} className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${getPhaseColor(pattern.errorPhase)}`}>
                          {pattern.errorPhase || 'unknown'}
                        </span>
                        <span className="text-sm font-semibold text-gray-900">
                          {pattern.errorName}
                        </span>
                        <span className="flex items-center gap-1 text-xs">
                          {pattern.trend === 'increasing' ? (
                            <TrendingUp className="w-3 h-3 text-red-500" />
                          ) : pattern.trend === 'decreasing' ? (
                            <TrendingDown className="w-3 h-3 text-green-500" />
                          ) : (
                            <Minus className="w-3 h-3 text-gray-400" />
                          )}
                          <span className={
                            pattern.trend === 'increasing' ? 'text-red-600' :
                            pattern.trend === 'decreasing' ? 'text-green-600' :
                            'text-gray-500'
                          }>
                            {pattern.trend}
                          </span>
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        {pattern.latestMessage}
                      </p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                        <span>{pattern.count} occurrences</span>
                        <span>{pattern.affectedSeeds.length} seeds affected</span>
                        <span>{pattern.affectedStores.length} stores affected</span>
                        <span>First: <TimeAgo timestamp={pattern.firstSeen} /></span>
                        <span>Last: <TimeAgo timestamp={pattern.lastSeen} /></span>
                      </div>
                    </div>
                    <div className="text-2xl font-bold text-gray-900 ml-4">
                      {pattern.count}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {activeTab === 'trace' && (
        <div className="space-y-4">
          {!traceCorrelationId ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
              <Code className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500">Enter a correlation ID above to view the full trace</p>
            </div>
          ) : traceLoading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-6 h-6 text-gray-400 animate-spin" />
            </div>
          ) : !trace ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
              <AlertCircle className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500">No trace found for this correlation ID</p>
            </div>
          ) : (
            <>
              {/* Seed Run Info */}
              {trace.seedRun && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <h3 className="text-sm font-medium text-gray-500 mb-4">Seed Run</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-xs text-gray-500">Seed</p>
                      <p className="font-medium">{trace.seedRun.seedName || trace.seedRun.seedId}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Store</p>
                      <p className="font-medium">{trace.seedRun.storeId}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Status</p>
                      <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                        trace.seedRun.status === 'success' ? 'bg-green-100 text-green-800' :
                        trace.seedRun.status === 'failed' ? 'bg-red-100 text-red-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {trace.seedRun.status}
                      </span>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Duration</p>
                      <p className="font-medium">{trace.seedRun.durationMs ? `${trace.seedRun.durationMs}ms` : '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Products Found</p>
                      <p className="font-medium">{trace.seedRun.productsFound || 0}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Started</p>
                      <p className="font-medium text-sm">{new Date(trace.seedRun.startedAt).toLocaleString()}</p>
                    </div>
                  </div>

                  {trace.seedRun.errorMessage && (
                    <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm text-red-800">{trace.seedRun.errorMessage}</p>
                    </div>
                  )}

                  {trace.seedRun.timingData && (
                    <div className="mt-4">
                      <h4 className="text-xs font-medium text-gray-500 mb-2">Timing Breakdown</h4>
                      <pre className="bg-gray-50 p-3 rounded-lg text-xs overflow-x-auto">
                        {JSON.stringify(trace.seedRun.timingData, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              )}

              {/* Logs */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-sm font-medium text-gray-500 mb-4">
                  Logs ({trace.logs.length})
                </h3>
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {trace.logs.map((log) => (
                    <div
                      key={log.id}
                      className={`p-2 rounded text-sm font-mono ${getLogLevelColor(log.logLevel)}`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-xs opacity-60">
                          {new Date(log.createdAt).toLocaleTimeString()}
                        </span>
                        <span className="uppercase text-xs font-bold">
                          {log.logLevel}
                        </span>
                        {log.timingOperation && (
                          <span className="text-xs bg-black/10 px-1 rounded">
                            {log.timingOperation}: {log.timingDurationMs}ms
                          </span>
                        )}
                      </div>
                      <p className="mt-1">{log.message}</p>
                      {log.context && Object.keys(log.context).length > 0 && (
                        <pre className="mt-1 text-xs opacity-70 overflow-x-auto">
                          {JSON.stringify(log.context, null, 2)}
                        </pre>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Errors */}
              {trace.errors.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <h3 className="text-sm font-medium text-gray-500 mb-4">
                    Errors ({trace.errors.length})
                  </h3>
                  <div className="space-y-4">
                    {trace.errors.map((error) => (
                      <div
                        key={error.id}
                        className="border border-red-200 bg-red-50 rounded-lg p-4"
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <AlertCircle className="w-4 h-4 text-red-600" />
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${getPhaseColor(error.errorPhase)}`}>
                            {error.errorPhase || 'unknown'}
                          </span>
                          <span className="font-medium text-red-900">{error.errorName}</span>
                        </div>
                        <p className="text-sm text-red-800 mb-2">{error.errorMessage}</p>
                        {error.errorStack && (
                          <pre className="bg-gray-900 text-gray-100 p-3 rounded text-xs overflow-x-auto max-h-48">
                            {error.errorStack}
                          </pre>
                        )}
                        {error.pageUrl && (
                          <p className="mt-2 text-xs text-gray-600">
                            URL: {error.pageUrl}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
